import React, { useState } from "react";
import {
  MdRefresh,
  MdWarning,
  MdChevronLeft,
  MdChevronRight,
  MdGridView,
  MdViewModule,
  MdViewComfy,
  MdViewList,
} from "react-icons/md";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/Empty";
import { CardSkeleton } from "@/components/ui/Skeleton";
import { useTaskList } from "@/features/dashboard/store/useTaskList";
import { useEventList } from "../store/useEventList";
import { EventCard } from "../components/EventCard";

// ─── Types ────────────────────────────────────────────────────────────────────

type GridSize = 1 | 2 | 3 | 4;

// ─── Grid config ──────────────────────────────────────────────────────────────

const GRID_OPTIONS: {
  size: GridSize;
  label: string;
  icon: React.ReactNode;
  className: string;
}[] = [
  {
    size: 1,
    label: "1 column",
    icon: <MdViewList size={16} />,
    className: "grid-cols-1",
  },
  {
    size: 2,
    label: "2 columns",
    icon: <MdGridView size={16} />,
    className: "grid-cols-1 sm:grid-cols-2",
  },
  {
    size: 3,
    label: "3 columns",
    icon: <MdViewModule size={16} />,
    className: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  },
  {
    size: 4,
    label: "4 columns",
    icon: <MdViewComfy size={16} />,
    className: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

interface StatItemProps {
  label: string;
  value: string | number;
}

const StatItem: React.FC<StatItemProps> = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-semibold text-gray-800">{value}</p>
  </div>
);

interface GridToggleProps {
  value: GridSize;
  onChange: (size: GridSize) => void;
}

const GridToggle: React.FC<GridToggleProps> = ({ value, onChange }) => (
  <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
    {GRID_OPTIONS.map((opt) => (
      <button
        key={opt.size}
        onClick={() => onChange(opt.size)}
        aria-label={opt.label}
        title={opt.label}
        className={`p-1.5 rounded-md transition-colors ${
          value === opt.size
            ? "bg-white text-indigo-600 shadow-sm"
            : "text-gray-400 hover:text-gray-600"
        }`}
      >
        {opt.icon}
      </button>
    ))}
  </div>
);

interface PaginationProps {
  pageNo: number;
  pageCount: number;
  loading: boolean;
  onPrev: () => void;
  onNext: () => void;
}

const Pagination: React.FC<PaginationProps> = ({
  pageNo,
  pageCount,
  loading,
  onPrev,
  onNext,
}) => {
  if (pageCount <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 pt-2">
      <button
        onClick={onPrev}
        disabled={pageNo <= 1 || loading}
        aria-label="Previous page"
        className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <MdChevronLeft size={18} className="text-gray-600" />
      </button>
      <span className="text-xs text-gray-500 min-w-[6rem] text-center tabular-nums">
        Page {pageNo} of {pageCount}
      </span>
      <button
        onClick={onNext}
        disabled={pageNo >= pageCount || loading}
        aria-label="Next page"
        className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <MdChevronRight size={18} className="text-gray-600" />
      </button>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const EventListPage: React.FC = () => {
  const { tasks, loading: tasksLoading } = useTaskList();
  const [gridSize, setGridSize] = useState<GridSize>(4);

  const {
    items,
    pageNo,
    pageCount,
    total,
    selectedTaskId,
    loading,
    error,
    selectTask,
    goToPage,
    refresh,
  } = useEventList();

  const hasData = items.length > 0;
  const showStats = !loading && total > 0;
  const gridClassName =
    GRID_OPTIONS.find((o) => o.size === gridSize)?.className ??
    "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

  return (
    <div className="space-y-2">
      <Card
        title="Event List"
        subtitle="Select a task to view detection events"
      >
        <div className="flex gap-3 flex-wrap items-center">
          <select
            value={selectedTaskId}
            onChange={(e) => selectTask(e.target.value)}
            disabled={tasksLoading || loading}
            aria-label="Select detection task"
            className="flex-1 min-w-48 border border-gray-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent bg-white text-gray-700 disabled:bg-gray-50 disabled:text-gray-400 transition-colors"
          >
            <option value="">— Select a task —</option>
            {Array.isArray(tasks) &&
              tasks.map((t) => (
                <option key={t.taskId} value={t.taskId}>
                  {t.deviceName ? `${t.deviceName} (${t.taskId})` : t.taskId}
                </option>
              ))}
          </select>

          {/* Grid toggle + Refresh dalam satu baris */}
          <div className="flex items-center gap-2 ml-auto">
            <GridToggle value={gridSize} onChange={setGridSize} />
            {selectedTaskId && (
              <button
                onClick={refresh}
                disabled={loading}
                aria-label="Refresh events"
                className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdRefresh
                  size={15}
                  className={loading ? "animate-spin" : ""}
                />
                Refresh
              </button>
            )}
          </div>
        </div>

        {showStats && (
          <div className="flex gap-6 mt-3 pt-3 border-t border-gray-50 flex-wrap">
            <StatItem label="Total Events" value={total} />
            <StatItem label="Page" value={`${pageNo} / ${pageCount}`} />
            <StatItem label="Showing" value={items.length} />
          </div>
        )}
      </Card>

      {!selectedTaskId ? (
        <EmptyState
          title="No task selected"
          description="Select a detection task above to start viewing events."
        />
      ) : error ? (
        <div
          role="alert"
          className="flex items-start gap-3 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-xl border border-red-200"
        >
          <MdWarning size={18} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Failed to load events</p>
            <p className="text-red-500 text-xs mt-0.5">{error}</p>
          </div>
        </div>
      ) : loading ? (
        <div className={`grid ${gridClassName} gap-4`}>
          {Array.from({ length: gridSize * 2 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : !hasData ? (
        <EmptyState
          title="No events found"
          description="No detection events recorded for this task yet."
        />
      ) : (
        <>
          <div className="max-h-[55vh] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
            <div className={`grid ${gridClassName} gap-4`}>
              {items.map((item) => (
                <EventCard key={item.id} item={item} />
              ))}
            </div>
          </div>

          <Pagination
            pageNo={pageNo}
            pageCount={pageCount}
            loading={loading}
            onPrev={() => goToPage(pageNo - 1)}
            onNext={() => goToPage(pageNo + 1)}
          />
        </>
      )}
    </div>
  );
};

export default EventListPage;
